import { z } from "zod";

// B3: deterministic TradingView alert specs derived from the visual
// builder input. The same function powers both the Pine `alertcondition`
// emission and the UI surface that shows users what to paste into
// TradingView's webhook configuration. Determinism is enforced by
// slugging the strategy name; two builds with the same input always
// emit the same alert names, which lets a user wire stable webhook
// rules without churn.

export type AlertSide = "long" | "short";

export type AlertSpec = {
  side: AlertSide;
  /** Title baked into Pine `alertcondition(..., name=...)`. */
  name: string;
  /** Default Pine message - includes TradingView placeholders. */
  message: string;
  /**
   * JSON object the user pastes into TradingView's "Message" field for
   * webhook delivery. TV substitutes {{...}} placeholders verbatim before
   * POSTing this body to the webhook URL.
   */
  payload: AlertPayload;
};

export type AlertPayload = z.infer<typeof alertPayloadSchema>;

// Schema is the single source of truth for what shape a webhook
// consumer can expect. It is also used to validate user-customized
// payloads when we add custom-payload editing.
export const alertPayloadSchema = z.object({
  schema_version: z.literal(1),
  strategy: z.string().min(1).max(120),
  alert: z.string().min(1).max(160),
  side: z.enum(["long", "short"]),
  action: z.enum(["buy", "sell"]),
  ticker: z.string(),
  interval: z.string(),
  time: z.string(),
  close: z.string(),
});

function slug(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "delta-x"
  );
}

function payloadFor(args: {
  base: string;
  side: AlertSide;
  alertName: string;
}): AlertPayload {
  return {
    schema_version: 1,
    strategy: args.base,
    alert: args.alertName,
    side: args.side,
    action: args.side === "long" ? "buy" : "sell",
    ticker: "{{ticker}}",
    interval: "{{interval}}",
    time: "{{time}}",
    close: "{{close}}",
  };
}

// Narrow input shape so the spec builder can be called from both the
// public PineStrategyBuilderInput and the normalized form used inside
// buildPineStrategy. Anything broader would couple alerts.ts back to
// strategy-builder.ts and reintroduce a circular import.
export type AlertSpecInput = {
  strategyName: string;
  includeAlerts: boolean;
};

/**
 * Builds the deterministic alert spec list for an input. Empty when
 * `includeAlerts === false`. Both long and short specs are always
 * returned when alerts are enabled: the `allowLong`/`allowShort` flags
 * only set the DEFAULT for TradingView's runtime input toggles, so a
 * user who flips a side back on inside TradingView still needs the
 * matching alertcondition to exist in the script.
 */
export function buildAlertSpecs(input: AlertSpecInput): AlertSpec[] {
  if (!input.includeAlerts) return [];
  const base = slug(input.strategyName);
  const longName = `${base}-long`;
  const shortName = `${base}-short`;
  return [
    {
      side: "long",
      name: longName,
      message: `${input.strategyName} long setup on {{ticker}} {{interval}}`,
      payload: payloadFor({ base, side: "long", alertName: longName }),
    },
    {
      side: "short",
      name: shortName,
      message: `${input.strategyName} short setup on {{ticker}} {{interval}}`,
      payload: payloadFor({ base, side: "short", alertName: shortName }),
    },
  ];
}

/**
 * Serializes an alert payload exactly the way a user should paste it into
 * TradingView's "Message" field: a single JSON object on one line with
 * deterministic key order (object literal order = insertion order in JS).
 * Pretty-printed for the UI display copy.
 */
export function stringifyAlertPayload(
  payload: AlertPayload,
  opts: { pretty?: boolean } = {},
): string {
  return opts.pretty
    ? JSON.stringify(payload, null, 2)
    : JSON.stringify(payload);
}

/**
 * Validates a (potentially user-edited) payload against the alert schema.
 * Returns the parsed value or null - callers can surface the zod error
 * directly when null.
 */
export function validateAlertPayload(value: unknown): AlertPayload | null {
  const parsed = alertPayloadSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
